import type { Iso2, Iti, UiTranslations } from 'intl-tel-input'
import { map, pipe } from 'remeda'

export const TEL_INPUT_VALIDATION_ERROR = 'Enter a valid telephone number'

export type TelInputValue =
  | { readonly status: 'empty' }
  | { readonly status: 'invalid' }
  | { readonly status: 'valid'; readonly value: string }

type LocaleModule = {
  readonly default: UiTranslations
}

export type TelInputControllerOptions = {
  readonly initialCountry: Iso2
  readonly locale: string
  readonly onlyCountries: readonly Iso2[]
  readonly dropdownParent?: HTMLElement
  readonly signal?: AbortSignal
}

export type TelInputController = {
  readonly setValue: (value: string | null | undefined) => void
  readonly getValue: () => TelInputValue
  readonly focus: () => void
  readonly destroy: () => void
}

const isLocaleModule = (value: unknown): value is LocaleModule =>
  typeof value === 'object' &&
  value !== null &&
  'default' in value &&
  typeof value.default === 'object' &&
  value.default !== null

const getTranslations = (module: unknown): UiTranslations => {
  if (!isLocaleModule(module)) {
    throw new TypeError('Invalid intl-tel-input locale module')
  }

  return module.default
}

const localeModules: Record<string, () => Promise<UiTranslations>> = {
  uk: () => import('intl-tel-input/locale/uk').then(getTranslations),
  en: () => import('intl-tel-input/locale/en').then(getTranslations)
}

const loadTranslations = (locale: string): Promise<UiTranslations> =>
  (localeModules[locale] ?? localeModules.en)()

const getDropdownParent = (telInput: HTMLInputElement): HTMLElement =>
  telInput.closest<HTMLElement>('.ag-popup, wa-dialog, wa-drawer') ??
  document.body

const abortedInitialization = (): Error =>
  new Error('Telephone input initialization aborted')

const waitForInitialization = (
  iti: Iti,
  signal: AbortSignal | undefined,
  destroy: () => void
): Promise<void> => {
  if (!signal) {
    return iti.promise
  }

  if (signal.aborted) {
    destroy()
    return Promise.reject(abortedInitialization())
  }

  return new Promise((resolve, reject) => {
    let settled = false

    const finish = (result: () => void): void => {
      if (settled) {
        return
      }

      settled = true
      signal.removeEventListener('abort', abort)
      result()
    }
    const abort = (): void =>
      finish(() => {
        destroy()
        reject(abortedInitialization())
      })

    signal.addEventListener('abort', abort, { once: true })
    iti.promise.then(
      () => finish(resolve),
      (error: unknown) => finish(() => reject(error))
    )
  })
}

export const parseCountryList = (raw: string | undefined): readonly Iso2[] =>
  raw
    ? pipe(
        raw.split(','),
        map((country) => country.trim().toLowerCase() as Iso2)
      )
    : []

// Loads the phone library only when a form field or grid editor needs it.
const initIntlTelInput = async (
  telInput: HTMLInputElement,
  options: TelInputControllerOptions
): Promise<Readonly<{ iti: Iti; destroy: () => void }>> => {
  const { initialCountry, locale, onlyCountries } = options
  const [{ default: intlTelInput }, uiTranslations] = await Promise.all([
    import('intl-tel-input/intlTelInputWithUtils'),
    loadTranslations(locale)
  ])

  if (options.signal?.aborted) {
    throw abortedInitialization()
  }

  const countryFilter =
    onlyCountries.length > 0
      ? { onlyCountries: [...onlyCountries] }
      : { excludeCountries: ['ru', 'by'] as Iso2[] }

  const iti = intlTelInput(telInput, {
    initialCountry,
    countrySelectorMode: 'AUTO',
    dropdownParent: options.dropdownParent ?? getDropdownParent(telInput),
    numberDisplayFormat: 'NATIONAL',
    separateDialCode: false,
    formatAsYouType: true,
    strictMode: true,
    strictRejectAnimation: false,
    countryOrder: ['ua'],
    ...countryFilter,
    countrySearch: true,
    countryNameLocale: locale,
    uiTranslations,
    allowedNumberTypes: ['MOBILE', 'FIXED_LINE', 'FIXED_LINE_OR_MOBILE']
  })
  let destroyed = false
  const destroy = (): void => {
    if (!destroyed) {
      destroyed = true
      iti.destroy()
    }
  }

  try {
    await waitForInitialization(iti, options.signal, destroy)
  } catch (error: unknown) {
    destroy()
    throw error
  }

  return { iti, destroy }
}

export const createTelInputController = async (
  telInput: HTMLInputElement,
  options: TelInputControllerOptions
): Promise<TelInputController> => {
  const { iti, destroy } = await initIntlTelInput(telInput, options)

  return {
    setValue: (value) => iti.setNumber(value ?? ''),
    getValue: () =>
      telInput.value.trim() === ''
        ? { status: 'empty' }
        : iti.isValidNumber() === true
          ? { status: 'valid', value: iti.getNumber() }
          : { status: 'invalid' },
    focus: () => telInput.focus(),
    destroy
  }
}
